import { useId, useState } from 'react'

export type SwitchProps = {
  /** Names the setting the switch turns on and off, such as "Email reminders". */
  label: string
  /** A sentence under the label that says what the setting does. */
  hint?: string
  /** On or off. Pass it to control the switch from the parent. */
  checked?: boolean
  /** The starting state when the switch keeps its own. Off by default. */
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  id?: string
}

/**
 * An on and off switch for a setting that takes effect at once. It is a native
 * checkbox with role="switch", so Space toggles it, the label toggles it, and
 * screen readers announce it as a switch that is on or off. The thumb moves
 * and shows a check when on, so the state never rests on color alone.
 */
export default function Switch({ label, hint, checked, defaultChecked = false, onChange, disabled = false, id }: SwitchProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = `${fieldId}-hint`
  const [own, setOwn] = useState(defaultChecked)
  const on = checked ?? own

  return (
    <div className="mds-switch-field">
      <label className="mds-switch" htmlFor={fieldId}>
        <input
          id={fieldId}
          type="checkbox"
          role="switch"
          className="mds-switch__input"
          checked={on}
          disabled={disabled}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => {
            setOwn(event.target.checked)
            onChange?.(event.target.checked)
          }}
        />
        <span className="mds-switch__track" aria-hidden="true">
          <span className="mds-switch__thumb" />
        </span>
        <span className="mds-switch__label">{label}</span>
      </label>
      {hint && (
        <span className="mds-hint mds-switch__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  )
}
